import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { CACHE_TIMES, DEFAULT_QUERY_OPTIONS } from './config';
import { fetchAnalyticsData, createAnalyticsUrl, serializeAnalyticsFilters } from './utils';
import type {
  BaseAnalyticsFilter,
  WellnessFilter,
} from '@repo/shared/dtos/analytics/AnalyticsFilterDto';

export interface AnalyticsQueryConfig {
  baseUrl: string;
  queryKeyPrefix: string;
  enabled?: boolean;
}

export interface QueryState<T> {
  data: T | undefined;
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  hasData: boolean;
  isEmpty: boolean;
  refetch: () => void;
  isSuccess: boolean;
}

export function buildAnalyticsQueryKey(
  endpoint: string,
  filters: BaseAnalyticsFilter | WellnessFilter,
  config: AnalyticsQueryConfig,
  additionalParams?: Record<string, string>,
) {
  const additionalParamsKey = additionalParams ? JSON.stringify(additionalParams) : '';
  const baseFilter = filters as BaseAnalyticsFilter;
  const granularityKey = baseFilter?.granularity ?? 'none';
  const serializedFilters = serializeAnalyticsFilters(baseFilter).toString();

  return [
    config.queryKeyPrefix,
    endpoint,
    granularityKey,
    serializedFilters,
    additionalParamsKey,
  ] as const;
}

export function useAnalyticsQuery<T>(
  endpoint: string,
  filters: BaseAnalyticsFilter,
  config: AnalyticsQueryConfig,
  additionalParams?: Record<string, string>,
): QueryState<T>;

export function useAnalyticsQuery<T>(
  endpoint: string,
  filters: WellnessFilter,
  config: AnalyticsQueryConfig,
  additionalParams?: Record<string, string>,
): QueryState<T>;

export function useAnalyticsQuery<T>(
  endpoint: string,
  filters: BaseAnalyticsFilter | WellnessFilter,
  config: AnalyticsQueryConfig,
  additionalParams?: Record<string, string>,
): QueryState<T> {
  const queryKey = useMemo(
    () => buildAnalyticsQueryKey(endpoint, filters, config, additionalParams),
    [endpoint, filters, config, additionalParams],
  );

  const query = useQuery<T>({
    queryKey,
    queryFn: ({ signal }) =>
      fetchAnalyticsData<T>(
        createAnalyticsUrl(
          config.baseUrl,
          endpoint,
          filters as BaseAnalyticsFilter,
          additionalParams,
        ),
        signal,
      ),
    enabled: config.enabled !== false,
    staleTime: CACHE_TIMES.STALE_TIME,
    gcTime: CACHE_TIMES.GC_TIME,
    ...DEFAULT_QUERY_OPTIONS,
  });

  return useMemo(
    () => ({
      ...query,
      hasData: Boolean(
        query.data && (Array.isArray(query.data) ? query.data.length > 0 : query.data),
      ),
      isEmpty: Boolean(
        query.data && (Array.isArray(query.data) ? query.data.length === 0 : !query.data),
      ),
      refetch: () => query.refetch(),
    }),
    [query],
  );
}
